/** Клиент трёх эндпойнтов онбординга (ТЗ §6). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../shared/api/http';
import type { OnboardingStateData, OnboardingTourState, ReplayTourInput, ReportProgressInput } from './types';

export const onboardingKeys = {
  state: ['onboarding'] as const,
};

/**
 * Состояние загружается после первой отрисовки и не влияет на TTI (NFR §11):
 * онбординг — надстройка, из-за которой основной интерфейс ждать не должен.
 */
export function useOnboardingState(enabled = true) {
  return useQuery({
    queryKey: onboardingKeys.state,
    queryFn: () => request<OnboardingStateData>('/api/v1/account/onboarding'),
    staleTime: 5 * 60_000,
    enabled,
    // Недоступный API онбординга не должен генерировать шторм ретраев: тур просто не покажем.
    retry: 1,
  });
}

export function useReportProgress() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tourId, ...body }: ReportProgressInput) =>
      request<OnboardingTourState>(`/api/v1/account/onboarding/${encodeURIComponent(tourId)}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (tour) => {
      // Обновляем кеш точечно: перезапрос состояния ради собственной же дельты избыточен.
      qc.setQueryData<OnboardingStateData>(onboardingKeys.state, (current) =>
        current
          ? {
              ...current,
              tours: [...current.tours.filter((t) => t.tourId !== tour.tourId), tour],
            }
          : current
      );
    },
  });
}

export function useReplayTour() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tourId, ...body }: ReplayTourInput) =>
      request<OnboardingTourState>(`/api/v1/account/onboarding/${encodeURIComponent(tourId)}/replay`, {
        method: 'POST',
        body: { trigger: 'user', ...body },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: onboardingKeys.state });
    },
  });
}
